import { useState, useMemo, useCallback, useRef, useLayoutEffect } from "react";
import { DEFAULT_CARD_GAP, DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH, DEFAULT_ROW_BUFFER_FOR_GRID } from "../constants/virtualization";
import { getModelConfig } from "../../utils/models";
import { scrollIntoViewWithinContainer } from "../utils/shared/scrollIntoView";

export const useGridVirtualization = ({
    items,
    cardWidth = DEFAULT_CARD_WIDTH,
    cardHeight = DEFAULT_CARD_HEIGHT,
    cardGap = DEFAULT_CARD_GAP,
    buffer = DEFAULT_ROW_BUFFER_FOR_GRID, // Row quantity to show as buffer
}) => {
    const containerRef = useRef(null);
    const scrollingRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [visibleWindow, setVisibleWindow] = useState({ startRow: 0, endRow: 2 });

    // Set container width (for resize changes, the grid changes the elements per row)
    useLayoutEffect(() => {
        const measureWidth = () => {
            if (containerRef?.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        measureWidth();
        window.addEventListener("resize", measureWidth);
        return () => window.removeEventListener("resize", measureWidth);
    }, [containerRef]);

    // Calculate grid dimensions
    const { columnsPerRow, totalRows, rowsInView } = useMemo(() => {
        const cols = Math.floor((containerWidth + cardGap) / (cardWidth + cardGap)) || 1;
        const rows = Math.ceil(items.length / cols);
        const viewportHeight = window.innerHeight * 0.7; // max-heigt:70vh of the space shown in window
        const visibleRows = Math.ceil(viewportHeight / (cardHeight + cardGap));

        return {
            columnsPerRow: cols,
            totalRows: rows,
            rowsInView: visibleRows,
        };
    }, [containerWidth, cardWidth, cardGap, items.length, cardHeight]);

    // Update visible window when dimensions change
    useLayoutEffect(() => {
        setVisibleWindow({
            startRow: 0,
            endRow: Math.min(rowsInView + buffer, totalRows),
        });
    }, [rowsInView, totalRows, buffer]);

    const onItemFocus = useCallback(
        ({ index: focusedIndex, node }) => {
            const focusedRow = Math.floor(focusedIndex / columnsPerRow);
            const currentViewportStart = visibleWindow.startRow + buffer;
            const currentViewportEnd = visibleWindow.endRow - buffer;

            let newStartRow = visibleWindow.startRow;
            let needsUpdate = false;

            // Check if focused row is outside the viewport (inside the buffer)
            if (focusedRow >= currentViewportEnd) {
                // Scrolling down - move window down
                const targetViewportStart = focusedRow - rowsInView + 1;
                newStartRow = Math.max(0, targetViewportStart - buffer);
                needsUpdate = true;
            } else if (focusedRow < currentViewportStart) {
                // Scrolling up - move window up
                newStartRow = Math.max(0, focusedRow - buffer);
                needsUpdate = true;
            }

            if (needsUpdate) {
                const newEndRow = Math.min(newStartRow + rowsInView + buffer * 2, totalRows);

                // Adjust start if at the bottom to maintain buffer
                if (newEndRow === totalRows && totalRows > rowsInView + buffer * 2) {
                    newStartRow = Math.max(0, totalRows - rowsInView - buffer * 2);
                }

                // Avoid re-calculations
                if (newStartRow !== visibleWindow.startRow || newEndRow !== visibleWindow.endRow) {
                    setVisibleWindow({ startRow: newStartRow, endRow: newEndRow });
                }
            }

            if (node) {
                if (getModelConfig().epg.customScrollBehavior) {
                    scrollIntoViewWithinContainer(node, 'eventsGridContainer');
                } else if (node.scrollIntoView) {
                    node.scrollIntoView({
                        behavior: "auto",
                        block: "nearest",
                    });
                }
            }
        },
        [visibleWindow, columnsPerRow, totalRows, rowsInView, buffer]
    );

    // Calculate which items to render (including buffer on both sides)
    const virtualItems = useMemo(() => {
        const startIndex = visibleWindow.startRow * columnsPerRow;
        const endIndex = Math.min(visibleWindow.endRow * columnsPerRow, items.length);

        return items.slice(startIndex, endIndex).map((item, index) => ({
            item,
            index: startIndex + index,
        }));
    }, [items, visibleWindow, columnsPerRow]);

    // Container style (reserve height for all the elements)
    const containerStyle = useMemo(
        () => ({
            height: `${totalRows * (cardHeight + cardGap) - cardGap}px`,
            position: "relative",
        }),
        [totalRows, cardHeight, cardGap]
    );

    // Grid style with positioning
    const gridStyle = useMemo(
        () => ({
            display: "grid",
            gridTemplateColumns: `repeat(${columnsPerRow}, ${cardWidth}px)`,
            gap: `${cardGap}px`,
            position: "absolute",
            top: `${visibleWindow.startRow * (cardHeight + cardGap)}px`,
            width: "100%",
        }),
        [columnsPerRow, cardWidth, cardGap, visibleWindow.startRow, cardHeight]
    );

    return {
        containerRef,
        scrollingRef,
        containerStyle,
        gridStyle,
        virtualItems,
        onItemFocus,
        columnsPerRow,
    };
};
